import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@apollo/client'

import { LIST_CUSTOM_SADDLE_ORDER_REQUESTS } from '../../../apollo/queries/customSaddleOrders'
import { UPDATE_STATUS } from '../../../apollo/mutations/updateTransactionStatus'
import { createToast } from '../../../utils/Toast'
import { useUserContext } from '../../../context/UserContext'
import { getLatestStatus } from './utils'
import {
    DEFAULT_STATUS_OPTIONS,
    CACTUS_STATUS_OPTIONS,
    CACTUS_VISIBLE_STATUSES,
    ITEMS_PER_PAGE,
} from './constants'

const Toast = createToast()

export function useCustomSaddleOrders() {
    const { isCactusAdmin, isSaddlefitAdmin } = useUserContext()

    // --- Filters ---
    const [statusFilter, setStatusFilter] = useState(null)
    const [shippingFilter, setShippingFilter] = useState(null)

    // --- Pagination ---
    const [tokens, setTokens] = useState([null])
    const [currentPage, setCurrentPage] = useState(0)

    // --- Modal state ---
    const [selectedOrder, setSelectedOrder] = useState(null)
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [editStatus, setEditStatus] = useState('')
    const [editNotes, setEditNotes] = useState('')

    // --- Status options (memoised) ---
    const statusOptions = useMemo(() => {
        return isCactusAdmin ? CACTUS_STATUS_OPTIONS : DEFAULT_STATUS_OPTIONS
    }, [isCactusAdmin])

    // --- Data query ---
    const { data, loading, error, refetch } = useQuery(LIST_CUSTOM_SADDLE_ORDER_REQUESTS, {
        variables: {
            status: statusFilter?.value || undefined,
            shippingDestination: shippingFilter?.value || undefined,
            limit: ITEMS_PER_PAGE,
            nextToken: tokens[currentPage],
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        onCompleted: (queryData) => {
            const nextToken = queryData?.listCustomSaddleOrderRequests?.nextToken
            if (nextToken && !tokens.includes(nextToken)) {
                setTokens((prev) => [...prev, nextToken])
            }
        },
    })

    // --- Mutation ---
    const [updateStatus, { loading: updateLoading }] = useMutation(UPDATE_STATUS, {
        onCompleted: () => {
            Toast.fire({ icon: 'success', title: 'Order updated successfully!' })
            setIsEditMode(false)
            setIsDetailModalOpen(false)
            refetch()
        },
        onError: (err) => {
            Toast.fire({ icon: 'error', title: err.message || 'Failed to update order' })
        },
    })

    const orders = useMemo(() => {
        let items = [...(data?.listCustomSaddleOrderRequests?.items || [])]

        items.sort((a, b) => {
            const dateA = Number(a.createdAt) || 0
            const dateB = Number(b.createdAt) || 0
            return dateB - dateA
        })

        if (isCactusAdmin && !statusFilter) {
            return items.filter((o) =>
                CACTUS_VISIBLE_STATUSES.includes(getLatestStatus(o))
            )
        }
        return items
    }, [data, isCactusAdmin, statusFilter])

    const totalCount = data?.listCustomSaddleOrderRequests?.count || 0

    const stats = useMemo(() => {
        const items = data?.listCustomSaddleOrderRequests?.items || []
        if (isCactusAdmin) {
            return {
                total: items.filter((o) => CACTUS_VISIBLE_STATUSES.includes(getLatestStatus(o))).length,
                received: items.filter((o) => getLatestStatus(o) === 'SUBMITTED_TO_CACTUS').length,
                processing: items.filter((o) => getLatestStatus(o) === 'PROCESSING').length,
                completed: items.filter((o) => getLatestStatus(o) === 'SHIPPED').length,
            }
        }
        return {
            total: totalCount,
            pending: items.filter((o) => getLatestStatus(o) === 'REQUESTED').length,
            approved: items.filter((o) => getLatestStatus(o) === 'APPROVED').length,
            inProduction: items.filter((o) =>
                ['SUBMITTED_TO_CACTUS', 'PROCESSING'].includes(getLatestStatus(o))
            ).length,
        }
    }, [data, isCactusAdmin, totalCount])

    // --- Pagination helpers ---
    const canLoadNext = !!data?.listCustomSaddleOrderRequests?.nextToken
    const canLoadPrevious = currentPage > 0

    const loadNextPage = () => {
        if (canLoadNext) setCurrentPage((prev) => prev + 1)
    }

    const loadPreviousPage = () => {
        if (canLoadPrevious) setCurrentPage((prev) => prev - 1)
    }

    // --- Filter handlers (reset pagination on change) ---
    const handleStatusFilterChange = (option) => {
        setStatusFilter(option)
        setTokens([null])
        setCurrentPage(0)
    }

    const handleShippingFilterChange = (option) => {
        setShippingFilter(option)
        setTokens([null])
        setCurrentPage(0)
    }

    // --- Modal handlers ---
    const handleViewOrder = (order) => {
        const currentStatus = getLatestStatus(order)
        setSelectedOrder(order)
        setEditStatus(currentStatus)
        setEditNotes(order.adminNotes || '')
        setIsDetailModalOpen(true)
        setIsEditMode(false)
    }

    const handleUpdateOrder = async () => {
        if (!selectedOrder) return
        await updateStatus({
            variables: {
                id: selectedOrder.id,
                status: editStatus,
                adminNotes: editNotes,
            },
            refetchQueries: ['ListCustomSaddleOrderRequests'],
        })
    }

    const closeDetailModal = () => setIsDetailModalOpen(false)

    return {
        // Context
        isCactusAdmin,
        isSaddlefitAdmin,

        // Data
        orders,
        loading,
        error,
        stats,

        // Filters
        statusFilter,
        shippingFilter,
        statusOptions,
        handleStatusFilterChange,
        handleShippingFilterChange,

        // Pagination
        canLoadNext,
        canLoadPrevious,
        loadNextPage,
        loadPreviousPage,

        // Modal
        selectedOrder,
        isDetailModalOpen,
        isEditMode,
        setIsEditMode,
        editStatus,
        setEditStatus,
        editNotes,
        setEditNotes,
        handleViewOrder,
        handleUpdateOrder,
        closeDetailModal,
        updateLoading,
    }
}
